import { useState } from "react";
import { ChevronDown, ChevronRight, Quote } from "lucide-react";

interface EvidenceRecord {
  evidence_id: string;
  source_text: string;
  semantic_candidates: { label: string; confidence: number }[];
}

interface Props {
  evidence: EvidenceRecord[];
  highlightedIds?: string[];
  onEvidenceHover?: (id: string | null) => void;
}

export default function EvidencePanel({ evidence, highlightedIds = [], onEvidenceHover }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="space-y-2 md:space-y-3">
      <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
        <Quote className="h-4 w-4 md:h-5 md:w-5 text-primary" />
        Evidence Layer
      </h3>
      <p className="text-xs md:text-sm text-muted-foreground">
        Raw language evidence extracted from your text
      </p>
      <div className="space-y-2">
        {evidence.map((ev) => {
          const isExpanded = expandedId === ev.evidence_id;
          const isHighlighted = highlightedIds.includes(ev.evidence_id);
          return (
            <div
              key={ev.evidence_id}
              className={`rounded-lg border p-2.5 md:p-3 transition-all cursor-pointer ${
                isHighlighted
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border bg-card hover:border-primary/40"
              }`}
              onClick={() => setExpandedId(isExpanded ? null : ev.evidence_id)}
              onMouseEnter={() => onEvidenceHover?.(ev.evidence_id)}
              onMouseLeave={() => onEvidenceHover?.(null)}
            >
              <div className="flex items-start gap-2">
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5 md:h-4 md:w-4 mt-0.5 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 md:h-4 md:w-4 mt-0.5 text-muted-foreground shrink-0" />
                )}
                <p className="text-xs md:text-sm font-medium text-foreground italic">
                  "{ev.source_text}"
                </p>
              </div>
              {isExpanded && (
                <div className="mt-2 md:mt-3 ml-5 md:ml-6 space-y-1.5 md:space-y-2">
                  {ev.semantic_candidates.map((sc, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                        {sc.label}
                      </span>
                      <div className="flex-1 h-1 md:h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${sc.confidence * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] md:text-xs text-muted-foreground w-8 md:w-10 text-right">
                        {Math.round(sc.confidence * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
